import { EventApi } from '@fullcalendar/core';
import { Draggable } from '@fullcalendar/interaction';
import React, { useEffect, useRef } from 'react';
import { FullCalendarEvent } from 'shared/components/ui/full-calendar-event';
import {
  PopupMenu,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import {
  getDraggableEventData,
  getEventIsPast,
} from 'shared/lib/full-calendar';
import { CalendarTaskSync } from 'shared/types/calendar-task-sync';
import { DataType } from 'shared/types/data-type';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { SmartList } from 'shared/types/smart-list';
import { Tag } from 'shared/types/tag';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';

import * as Styled from './more-popover.style';

export type FullCalendarMorePopoverProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children'
> & {
  weekStartsOn: WeekDays;
  events: EventApi[];
  tasks: Task[];
  goals: Goal[];
  lifeAreas: LifeArea[];
  tags: Tag[];
  smartLists: SmartList[];
  onComplete: (id: ID, date: Timestamp) => void;
  onClick?: (id: ID) => void;
  calendarSync?: CalendarTaskSync;
};

export const FullCalendarMorePopover: React.FC<
  FullCalendarMorePopoverProps
> = ({
  weekStartsOn,
  events,
  tasks,
  goals,
  lifeAreas,
  tags,
  smartLists,
  onComplete,
  onClick,
  onOutside,
  calendarSync,
  ...rest
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Recompute isPast (as the grid gets it from FullCalendar) so events in the
  // "+N more" popover fade the same way as the ones rendered in the grid.
  const now = new Date();

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const draggable = new Draggable(containerRef.current, {
      itemSelector: '[data-draggable="true"]',
      eventData: (el) => getDraggableEventData(el, { tasks, weekStartsOn }),
    });

    return () => draggable.destroy();
  }, [tasks, weekStartsOn]);

  return (
    <PopupMenu
      ref={containerRef}
      noBackdrop
      onOutside={onOutside}
      {...rest}
      ignoreSelector="[data-interaction-for-type]"
    >
      <PopupMenuList>
        {events.map((event) => {
          const isPast = getEventIsPast(event, now);

          return (
            <PopupMenuListItem
              key={event.id}
              data-draggable="true"
              data-component-type={
                event.extendedProps.taskId
                  ? DataType.Task
                  : event.extendedProps.lifeAreaId
                    ? DataType.LifeArea
                    : DataType.Goal
              }
              data-id={
                event.extendedProps.taskId ||
                event.extendedProps.lifeAreaId ||
                event.extendedProps.goalId
              }
              data-title={event.title}
            >
              <Styled.EventContainer>
                <FullCalendarEvent
                  event={event}
                  isPast={isPast}
                  tasks={tasks}
                  goals={goals}
                  lifeAreas={lifeAreas}
                  tags={tags}
                  smartLists={smartLists}
                  onComplete={onComplete}
                  onClick={onClick}
                  onInteractionComplete={onOutside}
                  calendarSync={calendarSync}
                />
              </Styled.EventContainer>
            </PopupMenuListItem>
          );
        })}
      </PopupMenuList>
    </PopupMenu>
  );
};
